import { useState, useMemo } from 'react';
import ProductCard from './ProductCard';
import { products } from '../../../mocks/products';

interface ProductGridProps {
  selectedCategory: string;
  selectedSizes: string[];
  priceRange: [number, number];
  sortBy: string;
}

export default function ProductGrid({
  selectedCategory,
  selectedSizes,
  priceRange,
  sortBy,
}: ProductGridProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 12;

  // Filter and sort products
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...products];

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    // Size filter
    if (selectedSizes.length > 0) {
      filtered = filtered.filter((p) =>
        p.sizes.some((size) => selectedSizes.includes(size))
      );
    }

    // Price filter
    filtered = filtered.filter(
      (p) => p.price >= priceRange[0] && p.price <= priceRange[1]
    );

    // Sort
    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => b.id - a.id);
        break;
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      default:
        // featured - keep original order
        break;
    }

    return filtered;
  }, [selectedCategory, selectedSizes, priceRange, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const currentProducts = filteredAndSortedProducts.slice(startIndex, endIndex);

  return (
    <div>
      {/* Results Count */}
      <div className="mb-8">
        <p className="text-sm text-gray-400">
          Showing <span className="text-white font-bold">{startIndex + 1}-{Math.min(endIndex, filteredAndSortedProducts.length)}</span> of{' '}
          <span className="text-white font-bold">{filteredAndSortedProducts.length}</span> products
        </p>
      </div>

      {/* Product Grid */}
      {currentProducts.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
            {currentProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-16 flex items-center justify-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-10 h-10 flex items-center justify-center text-white border border-white/20 rounded-lg hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-all whitespace-nowrap"
              >
                <i className="ri-arrow-left-s-line text-xl"></i>
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-bold transition-all whitespace-nowrap ${
                    currentPage === page
                      ? 'bg-white text-black'
                      : 'text-white border border-white/20 hover:bg-white/5'
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-10 h-10 flex items-center justify-center text-white border border-white/20 rounded-lg hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-all whitespace-nowrap"
              >
                <i className="ri-arrow-right-s-line text-xl"></i>
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="py-24 text-center">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-white/5 rounded-full">
            <i className="ri-search-line text-4xl text-gray-600"></i>
          </div>
          <h3 className="text-2xl font-black text-white mb-3">No Products Found</h3>
          <p className="text-gray-400 mb-8">Try adjusting your filters to see more results</p>
          <button
            onClick={() => window.location.reload()}
            className="px-8 py-3 bg-white text-black rounded-lg font-bold text-sm hover:scale-105 transition-all whitespace-nowrap"
          >
            RESET FILTERS
          </button>
        </div>
      )}
    </div>
  );
}